import { zodResolver } from '@hookform/resolvers/zod';
import { IonContent } from '@ionic/react';
import { Alert, AlertDescription } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';

import {
  AUTH_FIELD_LABEL_CLASS,
  AuthLayoutWrapper,
} from '@/core/components/auth/auth-layout-wrapper';
import { UsernameInput } from '@/core/components/auth/username-input';
import { useAuthFlow } from '@/core/hooks/use-auth-flow';
import { AppIonPage } from '@/core/components/app-ion-page';

export function CompletePage() {
  const location = useLocation();
  const { updateUserDetails, commonDict, userDetailsDict, errorsDict } =
    useAuthFlow();

  // Get user data from URL search params
  const searchParams = new URLSearchParams(location.search);
  const firstNameFromParams = searchParams?.get('firstName') || '';
  const lastNameFromParams = searchParams?.get('lastName') || '';
  const usernameFromParams = searchParams?.get('username') || '';

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const completeSchema = useMemo(
    () =>
      z.object({
        firstName: z.string().min(1, errorsDict.invalidFirstName),
        lastName: z.string().min(1, errorsDict.invalidLastName),
        username: z.string().optional(),
      }),
    [errorsDict.invalidFirstName, errorsDict.invalidLastName],
  );

  type CompleteFormData = z.infer<typeof completeSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<CompleteFormData>({
    mode: 'onTouched',
    resolver: zodResolver(completeSchema as never),
    defaultValues: {
      firstName: firstNameFromParams,
      lastName: lastNameFromParams,
      username: usernameFromParams,
    },
  });

  // Use useWatch instead of watch() to avoid React Compiler warnings
  const firstNameValue = useWatch({ control, name: 'firstName' });
  const lastNameValue = useWatch({ control, name: 'lastName' });
  const usernameValue = useWatch({ control, name: 'username' });

  const onSubmit = handleSubmit(async (values) => {
    setIsLoading(true);
    setError(null);

    const result = await updateUserDetails(
      values.firstName,
      values.lastName,
      values.username,
    );

    if (!result.success) {
      setError(result.error || errorsDict.requestFailed);
      setIsLoading(false);
    }
    // If successful, updateUserDetails already redirected to dashboard
  });

  return (
    <AppIonPage>
      <IonContent className="ion-padding" fullscreen scrollY={false}>
        <div className="bg-background text-foreground flex h-full w-full safe-area-padding">
          <AuthLayoutWrapper
            title={userDetailsDict.title}
            backTo="/auth/verify"
          >
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={onSubmit} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="firstName" className={AUTH_FIELD_LABEL_CLASS}>
                  {commonDict.firstNameLabel}
                </label>
                <Controller
                  control={control}
                  name="firstName"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      id="firstName"
                      type="text"
                      autoComplete="given-name"
                      placeholder={commonDict.firstNamePlaceholder}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                      onBlur={onBlur}
                    />
                  )}
                />
                {errors.firstName && (
                  <p className="text-[13.5px] text-destructive">
                    {errors.firstName.message}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="lastName" className={AUTH_FIELD_LABEL_CLASS}>
                  {commonDict.lastNameLabel}
                </label>
                <Controller
                  control={control}
                  name="lastName"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      id="lastName"
                      type="text"
                      autoComplete="family-name"
                      placeholder={commonDict.lastNamePlaceholder}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                      onBlur={onBlur}
                    />
                  )}
                />
                {errors.lastName && (
                  <p className="text-[13.5px] text-destructive">
                    {errors.lastName.message}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="username" className={AUTH_FIELD_LABEL_CLASS}>
                  {commonDict.usernameLabel}
                </label>
                <UsernameInput
                  id="username"
                  value={usernameValue || ''}
                  onChange={(value) => setValue('username', value)}
                  firstName={firstNameValue}
                  lastName={lastNameValue}
                  onProposalSelect={(value) => setValue('username', value)}
                  error={errors.username?.message}
                  disabled={isLoading}
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                radius="full"
                className="w-full"
                disabled={isLoading || !firstNameValue || !lastNameValue}
                isLoading={isLoading}
              >
                {userDetailsDict.update}
              </Button>
            </form>
          </AuthLayoutWrapper>
        </div>
      </IonContent>
    </AppIonPage>
  );
}
